import { useMemo, useState } from "react";
import { CalendarDays, ExternalLink, MapPin } from "lucide-react";
import { motion } from "framer-motion";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionHeading } from "@/components/SectionHeading";
import { CalendarLinks } from "@/components/CalendarLinks";
import { formatDate, formatDateRange, slugify } from "@/utils/format";

export function EventPosters({ events = [] }) {
  const modalities = useMemo(() => ["Todas", ...new Set(events.map((event) => event.modality))], [events]);
  const [filter, setFilter] = useState("Todas");
  const [selected, setSelected] = useState(null);
  const filtered = filter === "Todas" ? events : events.filter((event) => event.modality === filter);

  return (
    <section id="eventos" className="section-shell" data-testid="events-section">
      <SectionHeading number="01" eyebrow="Carteles y convocatorias" title="La temporada, en portada" description="Cada tirada se presenta como una pieza editorial: cartel grande, modalidad, fecha, ubicación y acceso directo a la información esencial." />

      <div className="mb-10 flex flex-wrap gap-3" data-testid="event-filter-group">
        {modalities.map((modality) => (
          <button
            key={modality}
            type="button"
            onClick={() => setFilter(modality)}
            className={`filter-chip ${filter === modality ? "filter-chip-active" : ""}`}
            data-testid={`event-filter-${slugify(modality)}`}
          >
            {modality}
          </button>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4" data-testid="events-grid">
        {filtered.map((event, index) => (
          <motion.article
            key={event.id}
            initial={{ opacity: 0, y: 38 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.07, duration: 0.65 }}
            className={`group poster-card ${event.featured ? "md:col-span-2" : ""}`}
            data-testid={`event-card-${event.id}`}
          >
            <div className="relative overflow-hidden">
              <img src={event.poster} alt={`Cartel de ${event.title}`} className={`w-full object-cover image-treatment transition-transform duration-700 group-hover:scale-105 ${event.featured ? "h-72" : "h-56"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
              <Badge className="absolute left-4 top-4 rounded-none border-clay/50 bg-charcoal/80 text-clay backdrop-blur" data-testid={`event-status-${event.id}`}>{event.status}</Badge>
            </div>
            <div className="p-6">
              <p className="eyebrow mb-3">{event.modality}</p>
              <h3 className="font-display text-4xl uppercase leading-[0.9] text-slate-50">{event.title}</h3>
              <div className="mt-5 space-y-2 text-sm text-slate-400">
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-clay" />{formatDateRange(event.date, event.end_date)} · {event.time}</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-clay" />{event.location}</p>
              </div>
              {event.results_links?.length ? <div className="mt-4 flex flex-wrap gap-2"><CalendarLinks item={event} prefix={`event-results-${event.id}`} /></div> : null}
              <Button type="button" variant="outline" onClick={() => setSelected(event)} className="mt-6 w-full rounded-none border-white/15 bg-white/[0.03] font-mono text-xs uppercase tracking-[0.2em] hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid={`event-open-${event.id}`}>
                Ver cartel
              </Button>
            </div>
          </motion.article>
        ))}
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl rounded-none border-white/10 bg-panel text-slate-100" data-testid="event-detail-dialog">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-4xl uppercase leading-none text-slate-50">{selected.title}</DialogTitle>
                <DialogDescription className="text-slate-400">{selected.modality} · {formatDate(selected.date)} · {selected.location}</DialogDescription>
              </DialogHeader>
              <img src={selected.poster} alt={`Cartel ampliado de ${selected.title}`} className="max-h-[52vh] w-full object-contain image-treatment" />
              <p className="leading-7 text-slate-300">{selected.description}</p>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass">
                  <a href={selected.poster_url || selected.poster} target="_blank" rel="noreferrer" data-testid="event-poster-download-link">
                    Abrir cartel <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
                <CalendarLinks item={selected} prefix="event-dialog-results" size="lg" />
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
